import React, { useState } from "react";
import {
  Box,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  TextFieldProps,
  SxProps,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { fieldBorder, fieldLabelColor } from "../../theme/theme";
import figmaClockIcon from "../../assets/icons/figma/clock.svg";
import LockOpenIcon from "../../assets/icons/unlock.svg";
import LockIcon from "../../assets/icons/lock.svg";

type Props = TextFieldProps & {
  label?: string;
  icon?: React.ReactNode;
  error?: boolean;
  helperText?: string;
  sx?: SxProps;
};

const TextInput = React.forwardRef<HTMLInputElement, Props>(
  (
    { label, icon, error, helperText, type, sx = { mb: 2.2 }, ...rest },
    ref
  ) => {
    const theme = useTheme();
    const [showPassword, setShowPassword] = useState(false);

    const isPassword = type === "password";
    const isTime = type === "time";
    const actualType = isPassword ? (showPassword ? "text" : "password") : type;

    // Time fields hide the native picker indicator and show the design-system
    // clock icon instead; clicking it still opens the native time picker.
    const openNativePicker = (e: React.MouseEvent<HTMLElement>) => {
      const input = e.currentTarget
        .closest(".MuiInputBase-root")
        ?.querySelector("input");
      try {
        (input as any)?.showPicker?.();
      } catch {
        // showPicker needs a user gesture / supported browser; focus as fallback
      }
      input?.focus();
    };

    const handleToggleVisibility = () => {
      setShowPassword((prev) => !prev);
    };

    return (
      <Box sx={sx}>
        {/* Static Label */}
        {label && (
          <Typography
            fontSize={14}
            fontWeight={600}
            mb={"8px"}
            sx={{ color: (t: any) => fieldLabelColor(t.palette.mode) }}
          >
            {label}
          </Typography>
        )}

        {/* Styled TextField */}
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          error={error}
          helperText={helperText}
          inputRef={ref}
          type={actualType}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {isPassword ? (
                  <IconButton
                    onClick={handleToggleVisibility}
                    sx={{
                      color: theme.palette.primary.main,
                      cursor: "pointer",
                    }}
                  >
                    {showPassword ? (
                      <img
                        src={LockOpenIcon}
                        alt="user"
                        style={{
                          width: 24,
                          height: 24,
                          objectFit: "contain",
                        }}
                      />
                    ) : (
                      <img
                        src={LockIcon}
                        alt="user"
                        style={{
                          width: 24,
                          height: 24,
                          objectFit: "contain",
                        }}
                      />
                    )}
                  </IconButton>
                ) : isTime && !icon ? (
                  <IconButton onClick={openNativePicker} sx={{ p: 0.5, mr: 0.5 }}>
                    <img src={figmaClockIcon} alt="" style={{ width: 18, height: 18 }} />
                  </IconButton>
                ) : (
                  icon
                )}
              </InputAdornment>
            ),
            sx: {
              borderRadius: "8px",
              fontSize: "14px",
              color: theme.palette.text.primary,
              paddingRight: icon || isTime ? "8px" : "0",
            },
          }}
          inputProps={{
            style: {
              // Responsive padding: less on lg and up, more on xs/sm/md
              padding: "13px 12px",
              fontSize: "12px",
            },
          }}
          sx={{
            "& input::placeholder, & textarea::placeholder": {
              color: "#A9A9A9",
              opacity: 1,
            },
            "& input[type='time']::-webkit-calendar-picker-indicator": {
              display: "none",
            },
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              "& fieldset": {
                borderColor: error
                  ? theme.palette.error.main
                  : fieldBorder(theme.palette.mode),
                borderWidth: "1px",
              },
              "&:hover fieldset": {
                borderColor: theme.palette.primary.main,
                borderWidth: "1px",
              },
              "&.Mui-focused fieldset": {
                borderColor: theme.palette.primary.main,
                borderWidth: "1px",
              },
              // Responsive input padding
              "& input": {
                padding: {
                  xs: "12px 12px",
                  sm: "12px 12px",
                  md: "12px 12px",
                  lg: "12px",
                  xl: "12px",
                },
                fontSize: {
                  xs: "13px",
                  sm: "13px",
                  md: "13px",
                  lg: "12px",
                  xl: "12px",
                },
                outline: "none",
                boxShadow: "none",
                "&:focus": {
                  outline: "none",
                  boxShadow: "none",
                },
                "&:focus-visible": {
                  outline: "none",
                  boxShadow: "none",
                },
              },
            },
            "& .MuiFormHelperText-root": {
              marginLeft: 0,
              fontSize: "12px",
            },
          }}
          {...rest}
        />
      </Box>
    );
  }
);

export default TextInput;
